import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileSpreadsheet, Loader2, MapPin, Navigation, RefreshCw, Search } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { toast } from 'sonner';
import {
  spotDriverLocationLogService,
  type SpotDriverLocationLog,
} from '../services/spotDriverLocationLogService';

const formatDateTimeTr = (value: string | null | undefined) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const normalizeSearchText = (value: unknown) =>
  String(value ?? '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/\s+/g, ' ')
    .trim();

type SortDirection = 'asc' | 'desc';

const COLUMNS = [
  { key: 'recordedAt', label: 'Tarih / Saat', width: 170, isDate: true },
  { key: 'cekiciPlaka', label: 'Çekici Plaka', width: 130 },
  { key: 'dorsePlaka', label: 'Dorse Plaka', width: 130 },
  { key: 'driverName', label: 'Sürücü Adı', width: 180 },
  { key: 'driverPhone', label: 'Telefon', width: 140 },
  { key: 'latitude', label: 'Enlem', width: 120, align: 'right' as const },
  { key: 'longitude', label: 'Boylam', width: 120, align: 'right' as const },
  { key: 'speed', label: 'Hız (km/h)', width: 100, align: 'right' as const },
  { key: 'accuracy', label: 'Doğruluk (m)', width: 120, align: 'right' as const },
  { key: 'locationDescription', label: 'Konum Açıklaması', width: 220 },
  { key: 'shipmentNo', label: 'Sevkiyat No', width: 140 },
  { key: 'actions', label: '', width: 120 },
] as const;

type ColumnKey = (typeof COLUMNS)[number]['key'];

export function SpotDriverLocationLogs() {
  const searchParams = new URLSearchParams(window.location.search);
  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);
  const monthStart = useMemo(() => `${today.slice(0, 8)}01`, [today]);

  const [from, setFrom] = useState(searchParams.get('from') || monthStart);
  const [to, setTo] = useState(searchParams.get('to') || today);
  const [plate, setPlate] = useState(searchParams.get('plate') || '');
  const [driverNameFilter, setDriverNameFilter] = useState('');
  const [data, setData] = useState<SpotDriverLocationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState<number | 'all'>('all');
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortKey, setSortKey] = useState<ColumnKey>('recordedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchText, setSearchText] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await spotDriverLocationLogService.getLogs({
        from,
        to,
        plate: plate.trim() || undefined,
        driverName: driverNameFilter.trim() || undefined,
        page,
        pageSize,
      });
      const rows = Array.isArray(response) ? response : (response?.data || []);
      const pagination = Array.isArray(response) ? null : response?.pagination;
      setData(rows);
      setTotalPages(pagination?.totalPages || 1);
      setTotal(pagination?.total || 0);
    } catch (err: any) {
      console.error('Spot sürücü konum logları yüklenemedi:', err);
      toast.error(err?.message || 'Veriler yüklenirken hata oluştu');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, plate, driverNameFilter, page, pageSize]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSort = (key: ColumnKey) => {
    if (key === 'actions') return;
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const filteredData = useMemo(() => {
    if (!searchText.trim()) return data;
    const normalized = normalizeSearchText(searchText);
    return data.filter((row) => {
      const searchable = normalizeSearchText(
        [row.cekiciPlaka, row.dorsePlaka, row.driverName, row.driverPhone, row.shipmentNo, row.locationDescription].join(' ')
      );
      return searchable.includes(normalized);
    });
  }, [data, searchText]);

  const sortedData = useMemo(() => {
    const rows = [...filteredData];
    rows.sort((a, b) => {
      const aVal = (a as any)[sortKey] ?? '';
      const bVal = (b as any)[sortKey] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal), 'tr-TR');
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [filteredData, sortKey, sortDirection]);

  const openInGoogleMaps = (lat: number, lng: number) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(
      sortedData.map((row) => ({
        'Tarih / Saat': formatDateTimeTr(row.recordedAt),
        'Çekici Plaka': row.cekiciPlaka,
        'Dorse Plaka': row.dorsePlaka,
        'Sürücü Adı': row.driverName,
        'Telefon': row.driverPhone,
        'Enlem': row.latitude,
        'Boylam': row.longitude,
        'Hız (km/h)': row.speed ?? '',
        'Doğruluk (m)': row.accuracy ?? '',
        'Konum Açıklaması': row.locationDescription || '',
        'Sevkiyat No': row.shipmentNo || '',
        'Google Maps Linki': `https://www.google.com/maps?q=${row.latitude},${row.longitude}`,
      }))
    );
    sheet['!cols'] = [
      { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 22 }, { wch: 14 },
      { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 24 },
      { wch: 14 }, { wch: 40 },
    ];
    XLSX.utils.book_append_sheet(workbook, sheet, 'Konum Logları');
    XLSX.writeFile(workbook, `spot-surucu-konum-loglari-${from}-${to}.xlsx`);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Spot Sürücü Konum Logları</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            Tarih aralığında spot sürücülerin enlem ve boylam konum loglarını görüntüleyin.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportExcel}
            disabled={loading || sortedData.length === 0}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Excele Aktar
          </button>
        </div>
      </div>

      {/* Filtre */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-slate-400">Başlangıç</label>
            <input
              type="date"
              value={from}
              onChange={(e) => { setFrom(e.target.value); setPage(1); }}
              className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-slate-400">Bitiş</label>
            <input
              type="date"
              value={to}
              onChange={(e) => { setTo(e.target.value); setPage(1); }}
              className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-slate-400">Plaka</label>
            <input
              type="text"
              value={plate}
              onChange={(e) => { setPlate(e.target.value); setPage(1); }}
              placeholder="Plaka ara..."
              className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-slate-400">Sürücü Adı</label>
            <input
              type="text"
              value={driverNameFilter}
              onChange={(e) => { setDriverNameFilter(e.target.value); setPage(1); }}
              placeholder="Sürücü adı ara..."
              className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => void loadData()}
              disabled={loading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Rapor Oluştur
            </button>
          </div>
        </div>
      </div>

      {/* Özet */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30">
            <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 dark:text-slate-400">Toplam Kayıt</div>
            <div className="text-xl font-bold text-gray-900 dark:text-slate-100">{total.toLocaleString('tr-TR')}</div>
          </div>
        </div>
        <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-green-50 dark:bg-green-900/30">
            <RefreshCw className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 dark:text-slate-400">Sayfa</div>
            <div className="text-xl font-bold text-gray-900 dark:text-slate-100">{page} / {totalPages}</div>
          </div>
        </div>
        <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-50 dark:bg-amber-900/30">
            <Navigation className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 dark:text-slate-400">Gösterilen</div>
            <div className="text-xl font-bold text-gray-900 dark:text-slate-100">{sortedData.length}</div>
          </div>
        </div>
      </div>

      {/* Arama */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Mevcut sayfada ara (plaka, sürücü, telefon...)"
            className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          />
        </div>
      </div>

      {/* Tablo */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-[#141414]">
        <div className="max-h-[65vh] overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className="cursor-pointer px-3 py-3 select-none whitespace-nowrap"
                    style={{ width: col.width }}
                    onClick={() => handleSort(col.key)}
                  >
                    <span className="flex items-center gap-1">
                      {col.label}
                      {sortKey === col.key && (
                        <span className="text-blue-600 dark:text-blue-400">
                          {sortDirection === 'asc' ? '\u2191' : '\u2193'}
                        </span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-16 text-center">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-500" />
                    <p className="mt-2 text-sm text-gray-500">Yükleniyor...</p>
                  </td>
                </tr>
              ) : sortedData.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-16 text-center text-sm text-gray-500">
                    Kayıt bulunamadı.
                  </td>
                </tr>
              ) : (
                sortedData.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="whitespace-nowrap px-3 py-2.5 text-sm text-gray-900 dark:text-slate-200">
                      {formatDateTimeTr(row.recordedAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 font-mono text-sm font-medium text-gray-900 dark:text-slate-200">
                      {row.cekiciPlaka}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 font-mono text-sm text-gray-600 dark:text-slate-400">
                      {row.dorsePlaka}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-sm font-medium text-gray-900 dark:text-slate-200">
                      {row.driverName}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-sm text-gray-600 dark:text-slate-400">
                      {row.driverPhone}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono text-xs text-gray-600 dark:text-slate-400">
                      {row.latitude.toFixed(6)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono text-xs text-gray-600 dark:text-slate-400">
                      {row.longitude.toFixed(6)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right text-sm text-gray-600 dark:text-slate-400">
                      {row.speed != null ? row.speed.toFixed(1) : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right text-sm text-gray-600 dark:text-slate-400">
                      {row.accuracy != null ? row.accuracy.toFixed(0) : '-'}
                    </td>
                    <td className="max-w-[220px] truncate px-3 py-2.5 text-sm text-gray-600 dark:text-slate-400">
                      {row.locationDescription || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-sm text-blue-600 dark:text-blue-400">
                      {row.shipmentNo || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => openInGoogleMaps(row.latitude, row.longitude)}
                        className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50"
                        title="Google Maps'te aç"
                      >
                        <Navigation className="h-3 w-3" />
                        Konuma Git
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Sayfalama */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
            <span className="text-xs text-gray-500 dark:text-slate-400">
              Toplam {total.toLocaleString('tr-TR')} kayıt
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="inline-flex h-8 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              >
                Önceki
              </button>
              <span className="text-xs text-gray-600 dark:text-slate-400">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="inline-flex h-8 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
